import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { dramas } from "@/mocks/dramas";
import DramaCard from "@/components/base/DramaCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";

const TYPES = ["全部", "电影", "电视剧"];
const RATINGS = [
  { label: "全部评分", value: 0 },
  { label: "8.0 分以上", value: 8 },
  { label: "7.5 分以上", value: 7.5 },
  { label: "7.0 分以上", value: 7 },
];
const SORTS = [
  { label: "按更新时间", value: "latest" },
  { label: "按人气", value: "popular" },
  { label: "按评分", value: "rating" },
];

const ALL_TAGS = Array.from(new Set(dramas.flatMap((d) => d.tags)));
const ALL_YEARS = Array.from(new Set(dramas.map((d) => d.year))).sort((a, b) => b - a);

const PAGE_SIZE = 6;

export default function Category() {
  useSeo({
    title: "影视分类 - 出轨的妻子影视大全",
    description:
      "出轨的妻子影视作品分类页，汇集家庭伦理、情感剧、悬疑、都市等题材的电影与电视剧，支持按类型、年份、评分和标签筛选，快速定位想看的剧情。",
    keywords: "出轨的妻子影视分类,家庭伦理剧,情感剧,电视剧筛选,电影列表,剧情推荐",
  });

  const [searchParams] = useSearchParams();
  const [type, setType] = useState(searchParams.get("type") || "全部");
  const [year, setYear] = useState(searchParams.get("year") || "全部");
  const [rating, setRating] = useState(Number(searchParams.get("rating")) || 0);
  const [tag, setTag] = useState(searchParams.get("tag") || "全部");
  const [sort, setSort] = useState("latest");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [panelOpen, setPanelOpen] = useState(false);

  const filtered = useMemo(() => {
    const list = dramas.filter((d) => {
      if (type !== "全部" && d.type !== type) return false;
      if (year !== "全部" && d.year !== Number(year)) return false;
      if (rating > 0 && d.rating < rating) return false;
      if (tag !== "全部" && !d.tags.includes(tag)) return false;
      return true;
    });
    return list.sort((a, b) => {
      if (sort === "popular") return b.popularity - a.popularity;
      if (sort === "rating") return b.rating - a.rating;
      return b.year - a.year || b.popularity - a.popularity;
    });
  }, [type, year, rating, tag, sort]);

  const shown = filtered.slice(0, visible);

  const reset = () => {
    setType("全部");
    setYear("全部");
    setRating(0);
    setTag("全部");
    setSort("latest");
    setVisible(PAGE_SIZE);
  };

  const update = <T,>(setter: (v: T) => void) => (value: T) => {
    setter(value);
    setVisible(PAGE_SIZE);
  };

  const FilterControls = () => (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-medium text-foreground-500 mb-2">类型</p>
        <div className="flex flex-wrap gap-2">
          {TYPES.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => update(setType)(t)}
              className={`rounded-full px-3.5 py-1 text-xs transition-colors whitespace-nowrap cursor-pointer ${
                type === t
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-300 text-foreground-700 hover:border-primary-300"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-medium text-foreground-500 mb-2">年份</p>
        <div className="flex flex-wrap gap-2">
          {["全部", ...ALL_YEARS.map(String)].map((y) => (
            <button
              key={y}
              type="button"
              onClick={() => update(setYear)(y)}
              className={`rounded-full px-3.5 py-1 text-xs transition-colors whitespace-nowrap cursor-pointer ${
                year === y
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-300 text-foreground-700 hover:border-primary-300"
              }`}
            >
              {y === "全部" ? "全部年份" : y}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-medium text-foreground-500 mb-2">评分区间</p>
        <div className="flex flex-wrap gap-2">
          {RATINGS.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => update(setRating)(r.value)}
              className={`rounded-full px-3.5 py-1 text-xs transition-colors whitespace-nowrap cursor-pointer ${
                rating === r.value
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-300 text-foreground-700 hover:border-primary-300"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-medium text-foreground-500 mb-2">题材标签</p>
        <div className="flex flex-wrap gap-2">
          {["全部", ...ALL_TAGS].map((tg) => (
            <button
              key={tg}
              type="button"
              onClick={() => update(setTag)(tg)}
              className={`rounded-full px-3.5 py-1 text-xs transition-colors whitespace-nowrap cursor-pointer ${
                tag === tg
                  ? "bg-secondary-500 text-background-50"
                  : "border border-background-300 text-foreground-700 hover:border-secondary-300"
              }`}
            >
              {tg === "全部" ? "全部标签" : tg}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视分类" }]} />

      <header className="mt-6 mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
          影视作品分类
        </h1>
        <p className="mt-3 text-sm md:text-base leading-7 text-foreground-600 max-w-3xl">
          这里收录了本站全部「出轨的妻子」题材影视作品，涵盖家庭伦理、情感剧、悬疑与都市等方向。
          你可以按类型、年份、评分与标签组合筛选，快速定位想看的剧情。
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Desktop filters */}
        <aside className="hidden lg:block lg:col-span-1">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 sticky top-20">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-base font-semibold text-foreground-950 flex items-center gap-2">
                <i className="ri-filter-3-line text-primary-500" />
                筛选条件
              </h2>
              <button
                type="button"
                onClick={reset}
                className="text-xs text-foreground-500 hover:text-accent-500 transition-colors cursor-pointer"
              >
                重置
              </button>
            </div>
            <FilterControls />
          </div>
        </aside>

        {/* Main list */}
        <div className="lg:col-span-3">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-2 text-sm text-foreground-600">
              <span>共找到</span>
              <span className="font-heading text-lg font-bold text-accent-600">{filtered.length}</span>
              <span>部作品</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPanelOpen(true)}
                className="lg:hidden inline-flex items-center gap-1.5 rounded-md border border-background-300 px-3.5 py-2 text-sm text-foreground-700 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <i className="ri-filter-3-line" />
                筛选
              </button>
              <div className="flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3 py-1.5">
                <i className="ri-sort-desc text-foreground-500 text-sm" />
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="bg-transparent text-sm text-foreground-800 outline-none cursor-pointer"
                >
                  {SORTS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {shown.length === 0 ? (
            <EmptyState
              icon="ri-search-eye-line"
              title="没有找到符合条件的影视作品"
              description="试试减少筛选条件，或直接重置后浏览全部作品。"
              actionLabel="重置筛选条件"
              onAction={reset}
            />
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
                {shown.map((drama) => (
                  <DramaCard key={drama.id} drama={drama} />
                ))}
              </div>
              {visible < filtered.length && (
                <div className="mt-8 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + PAGE_SIZE)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-add-line" />
                    加载更多（剩余 {filtered.length - visible} 部）
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile filter panel */}
      {panelOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] flex items-end">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setPanelOpen(false)}
            aria-hidden="true"
          />
          <div className="relative w-full max-h-[85vh] overflow-y-auto rounded-t-2xl bg-background-50 p-5 pb-8">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-lg font-semibold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setPanelOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-200 cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <FilterControls />
            <div className="mt-6 flex gap-3 sticky bottom-0 bg-background-50 pt-3">
              <button
                type="button"
                onClick={reset}
                className="flex-1 rounded-md border border-background-300 py-2.5 text-sm text-foreground-700 cursor-pointer"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setPanelOpen(false)}
                className="flex-1 rounded-md bg-primary-500 py-2.5 text-sm font-medium text-background-50 cursor-pointer"
              >
                查看 {filtered.length} 部作品
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}